import React, { useMemo } from 'react';
import { DialogProps } from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAfter } from 'date-fns';

import dayjs from 'utils/extendedDayJS';
import { Button } from '@epoch-ui/components/Button';
import { Form, DateTimePickerField } from '@epoch-ui/components/Form';
import { SwitchField } from '@epoch-ui/components/Form/fields/SwitchField';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter,
  ModalDescription
} from '@epoch-ui/components/Modal/modal';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { calculateInitialDateTimeSettings } from 'pages/dashboard/overview/events/create/utils';
import { PRETTY_DATE_TIME_TZ_FORMAT } from 'constants/dates';
import { convertSendAtToFormValue } from 'pages/dashboard/overview/events/schemas/commonUtils';
import {
  getStartOfDayInTimezone,
  interpretDateInTimezone,
  guessTimezone
} from 'utils/dateTimeUtils';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { updateRegistrationCutoff } from '@event-editor/api/update-registration-cutoff';

import {
  RegistrationCutoffModalFormValues,
  RegistrationCutoffModalFormInput,
  createRegistrationCutoffModalSchema,
  convertRegistrationCutoffDBToFormData
} from './RegistrationCutoffModal.schema';

export const RegistrationCutoffModal = ({
  onOpenChange,
  ...props
}: DialogProps): React.ReactElement => {
  const event = useEditableEvent();
  const {
    registrationCutoffAt,
    timezoneCode = guessTimezone(),
    datetimeEnd,
    datetimeScheduled
  } = event;
  const { showToast } = useToaster();
  const queryClient = useQueryClient();

  const form = useForm<
    RegistrationCutoffModalFormInput,
    unknown,
    RegistrationCutoffModalFormValues
  >({
    resolver: zodResolver(
      createRegistrationCutoffModalSchema({
        eventTimezone: timezoneCode,
        datetimeEnd
      })
    ),
    values: convertRegistrationCutoffDBToFormData(
      {
        hasRegistrationCutoffTime: !!registrationCutoffAt,
        registrationCutoffAt: registrationCutoffAt || null
      },
      timezoneCode
    ),
    mode: 'onChange'
  });

  const { watch, setValue, getValues, handleSubmit, formState } = form;

  const updateCutoffMutation = useMutation({
    mutationFn: updateRegistrationCutoff,
    onSuccess: (data) => {
      if (data.updateRegistrationCutoff?.ok) {
        queryClient.setQueryData(eventEditorQueryKeys.event(event.id!), (oldData: any) =>
          oldData
            ? {
                event: {
                  ...oldData.event,
                  registrationCutoffAt: data.updateRegistrationCutoff.event.registrationCutoffAt,
                  questionnaireCutoffAt: data.updateRegistrationCutoff.event.questionnaireCutoffAt
                }
              }
            : oldData
        );
        onOpenChange?.(false);
      }
    },
    onError: () => {
      showToast('Registration cutoff could not be updated.', { variant: ToasterVariant.ERROR });
    }
  });

  const hasRegistrationCutoffTime = watch('hasRegistrationCutoffTime');

  // Calculate date constraints for the date picker (with timezone consideration)
  const getCalendarDateDisabled = useMemo(() => {
    return (date: Date): boolean => {
      // Convert the calendar date to the event's timezone
      const dateInTimezone = interpretDateInTimezone(date, timezoneCode);

      // Disable if after event end date
      if (datetimeEnd) {
        const eventEndStartOfDay = getStartOfDayInTimezone(timezoneCode, datetimeEnd);
        if (isAfter(dateInTimezone, eventEndStartOfDay)) {
          return true;
        }
      }

      return false;
    };
  }, [timezoneCode, datetimeEnd]);

  const handleCutoffToggle = (checked: boolean): void => {
    setValue('hasRegistrationCutoffTime', checked, { shouldValidate: true, shouldDirty: true });
    if (!checked) {
      setValue('registrationCutoffAt', null, { shouldValidate: false, shouldDirty: true });
      return;
    }

    if (getValues('registrationCutoffAt')) {
      return;
    }

    // Default to event start time if available
    const newCutoff = datetimeScheduled
      ? convertSendAtToFormValue(datetimeScheduled, timezoneCode)
      : calculateInitialDateTimeSettings().startDateTime;
    setValue('registrationCutoffAt', newCutoff, { shouldValidate: false, shouldDirty: true });
  };

  const handleSave = handleSubmit(
    (data: RegistrationCutoffModalFormValues): void => {
      if (!event?.id) return;
      updateCutoffMutation.mutate({
        input: {
          eventId: event.id,
          registrationCutoffAt: data.hasRegistrationCutoffTime ? data.registrationCutoffAt : null
        }
      });
    },
    (errors) => {
      console.error(errors);
    }
  );

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <Form {...form}>
        <ModalContent className="tw-max-w-[470px]">
          <form onSubmit={handleSave}>
            <ModalHeader className="tw-mb-2">
              <ModalTitle className="tw-text-2xl tw-font-medium">Registration cutoff</ModalTitle>
            </ModalHeader>
            <ModalDescription className="tw-mb-5 tw-text-sm tw-text-muted-foreground">
              <div className="tw-flex tw-flex-col tw-gap-2">
                <span>
                  You can set a cutoff time to stop accepting new guests.{' '}
                  <a
                    href={'https://help.epochapp.com/articles/how-does-registration-cutoff-work'}
                    target="_blank"
                    rel="noreferrer"
                    className="tw-text-primary-grey tw-underline"
                  >
                    Learn more
                  </a>
                </span>
                <span>
                  Setting registration cutoff time will enable the option to set a questionnaire
                  cutoff time.
                </span>
              </div>
            </ModalDescription>

            <div>
              <SwitchField
                name="hasRegistrationCutoffTime"
                label="Set cutoff time"
                aria-label="Set cutoff time toggle"
                className="tw-mb-4 tw-flex-row-reverse tw-justify-between"
                onCheckedChange={handleCutoffToggle}
              />

              {hasRegistrationCutoffTime && (
                <div className="tw-flex tw-w-full tw-flex-col">
                  <DateTimePickerField name="registrationCutoffAt">
                    <DateTimePickerField.DateInput disabled={getCalendarDateDisabled} />
                    <DateTimePickerField.Time interval={60} />
                  </DateTimePickerField>
                  <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-leading-4 tw-text-muted-foreground">
                    {`You're in (${dayjs.getGMTOffset(timezoneCode)}) - ${dayjs
                      .getTimezoneName(timezoneCode)
                      .replace(/_/g, ' ')}`}
                  </p>
                  {datetimeScheduled && (
                    <p className="tw-mb-0 tw-text-sm tw-leading-4 tw-text-muted-foreground">
                      {`Event is scheduled to start on ${dayjs.formatPrettyDateTime({
                        dateTime: datetimeScheduled,
                        timezoneCode,
                        customFormat: PRETTY_DATE_TIME_TZ_FORMAT
                      })}`}
                    </p>
                  )}
                </div>
              )}
            </div>

            <ModalFooter className="tw-mt-5">
              <Button
                size="sm"
                className="tw-w-full tw-rounded-lg"
                type="submit"
                variant="primary"
                disabled={!formState.isDirty || updateCutoffMutation.isPending}
              >
                Save
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Form>
    </Modal>
  );
};
